'use client';

import { useEffect, useState } from 'react';
import { useLang } from '@/lib/i18n/LanguageContext';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  AreaChart,
  Area,
} from 'recharts';

interface OutcomeItem {
  name: string;
  count: number;
  fill: string;
}

interface ActivityItem {
  week: string;
  ideas: number;
}

interface PerformanceData {
  outcomes: { WIN: number; LOSS: number; BE: number; OPEN: number };
  weeklyActivity: ActivityItem[];
  totalTrades: number;
}

const OUTCOME_COLORS: Record<string, string> = {
  WIN: '#0e8a4d',
  LOSS: '#d93843',
  BE: '#b07c1f',
  OPEN: '#3e5fbf',
};

const CustomOutcomeTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      className="px-3 py-2.5 rounded-xl text-sm"
      style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        boxShadow: '0 8px 24px rgba(28,43,38,0.16)',
      }}
    >
      <p className="font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>
        {label}
      </p>
      <p style={{ color: payload[0]?.fill }}>
        {payload[0]?.value} trades
      </p>
    </div>
  );
};

const CustomActivityTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      className="px-3 py-2.5 rounded-xl text-sm"
      style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        boxShadow: '0 8px 24px rgba(28,43,38,0.16)',
      }}
    >
      <p className="text-xs mb-2 font-semibold" style={{ color: 'var(--text-muted)' }}>
        {label}
      </p>
      {payload.map((p: any) => (
        <p key={p.dataKey} className="text-xs" style={{ color: p.color }}>
          {p.name}: {p.value}
        </p>
      ))}
    </div>
  );
};

// Skeleton loader for charts
function ChartSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="h-4 w-32 rounded mb-3 skeleton" />
      <div className="h-40 rounded-lg skeleton" />
    </div>
  );
}

interface ProfilePerformanceChartProps {
  userId?: string;
}

export default function ProfilePerformanceChart({ userId }: ProfilePerformanceChartProps) {
  const { t } = useLang();
  const [data, setData] = useState<PerformanceData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const url = userId
      ? `/api/users/performance?userId=${encodeURIComponent(userId)}`
      : '/api/users/performance';

    fetch(url)
      .then((r) => r.json())
      .then((json) => {
        if (!json.error) setData(json);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId]);

  // Build outcome chart data
  const outcomeData: OutcomeItem[] = data
    ? Object.entries(data.outcomes).map(([name, count]) => ({
        name,
        count: count as number,
        fill: OUTCOME_COLORS[name] ?? '#8c9891',
      }))
    : [];

  const activityData = data?.weeklyActivity ?? [];
  const total = data?.totalTrades ?? 0;
  const winCount = data?.outcomes.WIN ?? 0;
  const lossCount = data?.outcomes.LOSS ?? 0;
  const beCount = data?.outcomes.BE ?? 0;
  const closedCount = winCount + lossCount + beCount;
  const winRate = closedCount > 0 ? Math.round((winCount / closedCount) * 100) : 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Outcome distribution */}
      <div className="card-base p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3
              className="text-sm font-semibold"
              style={{ color: 'var(--text-primary)' }}
            >
              {t('Trade Outcomes')}
            </h3>
            <p className="text-xs" style={{ color: 'var(--text-dim)' }}>
              {loading ? '…' : `${total} ${t('total trades')}`}
            </p>
          </div>
          {!loading && (
            <div className="flex items-center gap-3">
              {outcomeData.slice(0, 3).map((d) => (
                <div key={d.name} className="flex items-center gap-1.5">
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ background: d.fill }}
                  />
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    {d.name}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {loading ? (
          <ChartSkeleton />
        ) : total === 0 ? (
          <div
            className="flex items-center justify-center h-40 rounded-lg"
            style={{ background: 'var(--bg-sunken)' }}
          >
            <p className="text-sm" style={{ color: 'var(--text-dim)' }}>
              {t('No trades yet')}
            </p>
          </div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={160}>
              <BarChart
                data={outcomeData}
                barSize={40}
                margin={{ top: 4, right: 4, bottom: 4, left: -20 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="var(--border)"
                  vertical={false}
                />
                <XAxis
                  dataKey="name"
                  tick={{ fill: 'var(--text-muted)', fontSize: 11, fontFamily: 'Instrument Sans' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: 'var(--text-muted)', fontSize: 11, fontFamily: 'Instrument Sans' }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip content={<CustomOutcomeTooltip />} cursor={{ fill: 'var(--bg-sunken)' }} />
                <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                  {outcomeData.map((entry) => (
                    <Cell key={entry.name} fill={entry.fill} fillOpacity={0.85} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>

            {/* Win rate bar */}
            <div className="mt-4">
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span style={{ color: 'var(--text-muted)' }}>{t('Win rate across closed trades')}</span>
                <span className="font-semibold font-mono" style={{ color: 'var(--bullish)' }}>
                  {winRate}%
                </span>
              </div>
              <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--border)' }}>
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{
                    width: `${winRate}%`,
                    background: 'linear-gradient(90deg, var(--bullish), var(--accent))',
                  }}
                />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Activity over time */}
      <div className="card-base p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3
              className="text-sm font-semibold"
              style={{ color: 'var(--text-primary)' }}
            >
              {t('Activity (Last 16 Weeks)')}
            </h3>
            <p className="text-xs" style={{ color: 'var(--text-dim)' }}>
              {t('Ideas posted per week')}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ background: 'var(--accent)' }} />
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{t('Ideas')}</span>
            </div>
          </div>
        </div>

        {loading ? (
          <ChartSkeleton />
        ) : activityData.every((w) => w.ideas === 0) ? (
          <div
            className="flex items-center justify-center h-40 rounded-lg"
            style={{ background: 'var(--bg-sunken)' }}
          >
            <p className="text-sm" style={{ color: 'var(--text-dim)' }}>
              {t('No activity yet')}
            </p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={160}>
            <AreaChart
              data={activityData}
              margin={{ top: 4, right: 4, bottom: 4, left: -20 }}
            >
              <defs>
                <linearGradient id="ideasGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0c5b41" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#0c5b41" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="var(--border)"
                vertical={false}
              />
              <XAxis
                dataKey="week"
                tick={{ fill: 'var(--text-muted)', fontSize: 9, fontFamily: 'Instrument Sans' }}
                axisLine={false}
                tickLine={false}
                interval={3}
              />
              <YAxis
                tick={{ fill: 'var(--text-muted)', fontSize: 10, fontFamily: 'Instrument Sans' }}
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
              />
              <Tooltip content={<CustomActivityTooltip />} />
              <Area
                type="monotone"
                dataKey="ideas"
                name={t('Ideas')}
                stroke="#0c5b41"
                strokeWidth={2}
                fill="url(#ideasGrad)"
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}